import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { submitKontakt } from '../services/hubspot';
import { CheckCircle, XCircle, Phone } from 'lucide-react';
import PrivacyCheckbox from './PrivacyCheckbox';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!privacyAccepted) return;
    
    setLoading(true);
    setStatus(null);

    const hubspotResult = await submitKontakt({
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      subject: formData.subject,
      message: formData.message
    });

    try {
      await axios.post(`${API}/api/contact`, { ...formData, privacy_accepted: true });
    } catch (error) {
      console.error('Backend submission error:', error);
    }

    setLoading(false);
    
    if (hubspotResult.success) {
      setStatus('success');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setPrivacyAccepted(false);
    } else {
      setStatus('error');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const StatusMessage = () => (
    <AnimatePresence>
      {status && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className={`p-4 mb-6 flex items-start gap-3 ${
            status === 'success' 
              ? 'bg-green-500/10 border border-green-500/30' 
              : 'bg-red-500/10 border border-red-500/30'
          }`}
        >
          {status === 'success' ? (
            <>
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-green-400 font-medium">Vielen Dank!</p>
                <p className="text-green-400/80 text-sm">Serdar Dogan meldet sich in Kürze bei Ihnen.</p>
              </div>
            </>
          ) : (
            <>
              <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-red-400 font-medium">Es ist ein Fehler aufgetreten.</p>
                <p className="text-red-400/80 text-sm">
                  Bitte rufen Sie uns direkt an: 
                  <a href="tel:+491756900374" className="ml-1 inline-flex items-center gap-1 text-[#d1af6a] hover:underline">
                    <Phone className="w-3 h-3" /> +49 175 6900374
                  </a>
                </p>
              </div>
            </>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
      <StatusMessage />
      
      {status !== 'success' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              type="text"
              name="name"
              placeholder="Ihr Name *"
              required
              value={formData.name}
              onChange={handleChange}
              className="input-luxury w-full"
            />
            <input
              type="email"
              name="email"
              placeholder="E-Mail *"
              required
              value={formData.email}
              onChange={handleChange}
              className="input-luxury w-full"
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              type="tel"
              name="phone"
              placeholder="Telefonnummer"
              value={formData.phone}
              onChange={handleChange}
              className="input-luxury w-full"
            />
            <Select
              value={formData.subject}
              onValueChange={(value) => setFormData(prev => ({ ...prev, subject: value }))}
            >
              <SelectTrigger className="input-luxury w-full h-auto">
                <SelectValue placeholder="Betreff *" />
              </SelectTrigger>
              <SelectContent className="bg-[#1a2640] border-[#d1af6a]/20">
                <SelectItem value="Wertermittlung">Wertermittlung</SelectItem>
                <SelectItem value="Verkauf">Verkauf</SelectItem>
                <SelectItem value="Vermietung">Vermietung</SelectItem>
                <SelectItem value="Vermittlung">Vermittlung</SelectItem>
                <SelectItem value="Sonstiges">Sonstiges</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <textarea
            name="message"
            placeholder="Ihre Nachricht *"
            required
            rows={5}
            value={formData.message}
            onChange={handleChange}
            className="input-luxury w-full resize-none"
          />

          <PrivacyCheckbox 
            checked={privacyAccepted} 
            onCheckedChange={setPrivacyAccepted} 
          />

          <button
            type="submit"
            disabled={loading || !privacyAccepted}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="contact-submit"
          >
            {loading ? 'Wird gesendet…' : 'Nachricht senden'}
          </button>
        </>
      )}
    </form>
  );
}
